import React, { useEffect, useState } from 'react';
import { FaDownload, FaHandHoldingHeart, FaRedo, FaRupeeSign } from 'react-icons/fa';
import SEO from '../components/SEO';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';

const LIC_PLAN_TYPES = {
  endowment: {
    label: 'Endowment Plan',
    description: 'A savings-oriented LIC plan with maturity value at the end of the policy term.',
    bonusRatePerThousand: 40,
    loyaltyAddition: 0,
    yearlyBenefitPercent: 0,
    benefitIntervalYears: 0,
  },
  moneyback: {
    label: 'Money Back Plan',
    description: 'A plan that pays periodic survival benefits during the policy term and a balance at maturity.',
    bonusRatePerThousand: 38,
    loyaltyAddition: 0,
    yearlyBenefitPercent: 20,
    benefitIntervalYears: 5,
  },
  singlepremium: {
    label: 'Single Premium Plan',
    description: 'A one-time premium plan where the policyholder pays once and receives maturity value later.',
    bonusRatePerThousand: 35,
    loyaltyAddition: 0,
    yearlyBenefitPercent: 0,
    benefitIntervalYears: 0,
  },
};

const LICPlanCalculator = () => {
  const [planType, setPlanType] = useState('endowment');
  const [annualPremium, setAnnualPremium] = useState(50000);
  const [policyTermYears, setPolicyTermYears] = useState(20);
  const [sumAssured, setSumAssured] = useState(1000000);
  const [bonusRatePerThousand, setBonusRatePerThousand] = useState(40);
  const [loyaltyAddition, setLoyaltyAddition] = useState(0);
  const [result, setResult] = useState(null);

  const selectedPlan = LIC_PLAN_TYPES[planType];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [planType, annualPremium, policyTermYears, sumAssured, bonusRatePerThousand, loyaltyAddition]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const premium = Number(annualPremium) || 0;
    const termYears = Number(policyTermYears) || 0;
    const assured = Number(sumAssured) || 0;
    const bonusRate = Number(bonusRatePerThousand) || 0;
    const loyalty = Number(loyaltyAddition) || 0;

    const totalPremiumPaid = planType === 'singlepremium' ? premium : premium * termYears;
    const yearlyBonus = (assured / 1000) * bonusRate;
    const totalBonus = yearlyBonus * termYears;

    let totalSurvivalBenefits = 0;
    let payoutCount = 0;
    let maturitySumAssured = assured;

    if (planType === 'moneyback' && selectedPlan.benefitIntervalYears > 0) {
      payoutCount = Math.max(0, Math.floor(termYears / selectedPlan.benefitIntervalYears) - 1);
      totalSurvivalBenefits = payoutCount * (assured * (selectedPlan.yearlyBenefitPercent / 100));
      maturitySumAssured = Math.max(0, assured - totalSurvivalBenefits);
    }

    const maturityValue = maturitySumAssured + totalBonus + loyalty;
    const totalExpectedReturn = maturityValue + totalSurvivalBenefits;
    const gains = totalExpectedReturn - totalPremiumPaid;
    const returnPercent = totalPremiumPaid > 0 ? (gains / totalPremiumPaid) * 100 : 0;

    const yearlyBreakdown = [];
    let cumulativeBonus = 0;
    let cumulativeSurvival = 0;

    for (let year = 1; year <= termYears; year += 1) {
      cumulativeBonus += yearlyBonus;
      const survivalPayout = planType === 'moneyback' && selectedPlan.benefitIntervalYears > 0 && year % selectedPlan.benefitIntervalYears === 0 && year < termYears
        ? assured * (selectedPlan.yearlyBenefitPercent / 100)
        : 0;
      cumulativeSurvival += survivalPayout;

      yearlyBreakdown.push({
        year,
        bonus: Math.round(cumulativeBonus),
        survivalPayout: Math.round(survivalPayout),
        cumulativeSurvival: Math.round(cumulativeSurvival),
        estimatedValue: Math.round(totalPremiumPaid / Math.max(1, termYears) * year + cumulativeBonus + cumulativeSurvival),
      });
    }

    setResult({
      totalPremiumPaid,
      yearlyBonus: Math.round(yearlyBonus),
      totalBonus: Math.round(totalBonus),
      totalSurvivalBenefits: Math.round(totalSurvivalBenefits),
      maturitySumAssured: Math.round(maturitySumAssured),
      maturityValue: Math.round(maturityValue),
      totalExpectedReturn: Math.round(totalExpectedReturn),
      gains: Math.round(gains),
      returnPercent,
      payoutCount,
      yearlyBreakdown,
    });
  };

  const handleReset = () => {
    setPlanType('endowment');
    setAnnualPremium(50000);
    setPolicyTermYears(20);
    setSumAssured(1000000);
    setBonusRatePerThousand(40);
    setLoyaltyAddition(0);
  };

  const handleDownloadPDF = () => {
    if (!result) return;

    generateGenericPDF('LIC Plan Calculation Report', {
      'Plan Type': selectedPlan.label,
      'Annual Premium': planType === 'singlepremium' ? 'Single Premium' : formatCurrency(annualPremium),
      'Policy Term': `${policyTermYears} years`,
      'Sum Assured': formatCurrency(sumAssured),
      'Bonus Rate (per 1000)': bonusRatePerThousand,
      'Total Premium Paid': formatCurrency(result.totalPremiumPaid),
      'Estimated Bonus': formatCurrency(result.totalBonus),
      'Survival Benefits': formatCurrency(result.totalSurvivalBenefits),
      'Maturity Value': formatCurrency(result.maturityValue),
      'Total Expected Return': formatCurrency(result.totalExpectedReturn),
      'Estimated Gain': formatCurrency(result.gains),
      'Return %': `${result.returnPercent.toFixed(2)}%`,
    });
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="LIC Plan Calculator"
        description="Free LIC plan calculator to estimate premium paid, bonus, maturity value, and expected returns for endowment and money-back style plans."
        keywords="LIC plan calculator, LIC maturity calculator, LIC bonus calculator, LIC premium calculator, endowment plan calculator, money back plan calculator"
        canonicalPath="/lic-plan"
        calculatorType="LIC Plan Calculator"
        calculatorDescription="Estimate LIC premium, bonus, maturity value, and expected returns for common LIC plan styles."
        faqItems={[
          {
            question: 'What does the LIC Plan Calculator estimate?',
            answer: 'It estimates total premium paid, accrued bonus, maturity value, and expected return for common LIC plan styles.',
          },
          {
            question: 'Is this the exact LIC payout?',
            answer: 'No. LIC bonuses and benefits vary by specific policy, issue year, and declared rates, so this is only an estimate.',
          },
          {
            question: 'Can I use it for money-back plans?',
            answer: 'Yes. The calculator includes a money-back style estimate with periodic survival benefits and a maturity amount.',
          },
          {
            question: 'Should I verify the policy brochure separately?',
            answer: 'Yes. Always check the policy brochure and LIC illustrations before making final investment decisions.',
          },
        ]}
      />

      <div className="calculator-header">
        <h1><FaHandHoldingHeart style={{ color: '#8b5cf6' }} /> LIC Plan Calculator</h1>
        <p>Estimate premium paid, bonus, maturity value, and expected returns for LIC-style savings plans.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Plan Type</label>
            <div className="tabs">
              {Object.entries(LIC_PLAN_TYPES).map(([key, config]) => (
                <button
                  key={key}
                  className={`tab ${planType === key ? 'active' : ''}`}
                  onClick={() => setPlanType(key)}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          <div className="info-box" style={{ marginBottom: '20px' }}>
            <h4>{selectedPlan.label}</h4>
            <p style={{ margin: 0 }}>{selectedPlan.description}</p>
          </div>

          <div className="input-group">
            <label>{planType === 'singlepremium' ? 'Single Premium (₹)' : 'Annual Premium (₹)'}</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={annualPremium === '' ? '' : annualPremium}
                onChange={(e) => setAnnualPremium(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Policy Term (Years)</label>
            <input
              type="number"
              value={policyTermYears === '' ? '' : policyTermYears}
              onChange={(e) => setPolicyTermYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              step="1"
            />
          </div>

          <div className="input-group">
            <label>Sum Assured (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={sumAssured === '' ? '' : sumAssured}
                onChange={(e) => setSumAssured(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Bonus Rate per ₹1000 Sum Assured</label>
            <input
              type="number"
              value={bonusRatePerThousand === '' ? '' : bonusRatePerThousand}
              onChange={(e) => setBonusRatePerThousand(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="0.1"
            />
          </div>

          <div className="input-group">
            <label>Loyalty Addition / Final Addition (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={loyaltyAddition === '' ? '' : loyaltyAddition}
                onChange={(e) => setLoyaltyAddition(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <h3>Total Expected Return</h3>
                <div className="amount">{formatCurrency(result.totalExpectedReturn)}</div>
                <small>Estimated gain: {formatCurrency(result.gains)} | Return: {result.returnPercent.toFixed(2)}%</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Premium Paid</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.totalPremiumPaid)}</span>
                </div>
                <div className="result-item" style={{ background: '#f5f3ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Maturity Value</span>
                  <span className="value" style={{ color: '#7c3aed' }}>{formatCurrency(result.maturityValue)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Estimated Annual Bonus</span>
                  <span className="value">{formatCurrency(result.yearlyBonus)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Bonus</span>
                  <span className="value">{formatCurrency(result.totalBonus)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Survival Benefits</span>
                  <span className="value">{formatCurrency(result.totalSurvivalBenefits)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Policy Term</span>
                  <span className="value">{policyTermYears} years</span>
                </div>
              </div>

              <div className="breakup-section">
                <h4>Yearly Projection</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Cumulative Bonus</th>
                      <th>Survival Benefit</th>
                      <th>Cumulative Survival</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.yearlyBreakdown.slice(0, 10).map((row) => (
                      <tr key={row.year}>
                        <td>{row.year}</td>
                        <td>{formatCurrency(row.bonus)}</td>
                        <td>{formatCurrency(row.survivalPayout)}</td>
                        <td>{formatCurrency(row.cumulativeSurvival)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>Important Note</h4>
        <ul>
          <li>LIC bonuses are declared by the corporation and vary by policy and issue year.</li>
          <li>This calculator gives a planning estimate, not an official policy illustration.</li>
          <li>Money-back survival benefits are simplified here for quick comparison.</li>
        </ul>
      </div>
    </div>
  );
};

export default LICPlanCalculator;